import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import ReleaseGate from "@/components/ReleaseGate";
import SyncStatus from "@/components/SyncStatus";
import OfflineErrorToaster from "@/components/OfflineErrorToaster";
import BrandSplash from "@/components/BrandSplash";

import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import PersonalSignup from "@/pages/PersonalSignup";
import MyCaterCTRL from "@/pages/MyCaterCTRL";
import Perks from "@/pages/Perks";
import SetPassword from "@/pages/SetPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Orders from "@/pages/Orders";
import Deliveries from "@/pages/Deliveries";
import Recipes from "@/pages/Recipes";
import Menus from "@/pages/Menus";
import Temperatures from "@/pages/Temperatures";
import Cleaning from "@/pages/Cleaning";
import Prep from "@/pages/Prep";
import FoodSafetySetup from "@/pages/FoodSafetySetup";
import Checklists from "@/pages/Checklists";
import Workforce from "@/pages/Workforce";
import MyRota from "@/pages/MyRota";
import MyTasks from "@/pages/MyTasks";
import Staff from "@/pages/Staff";
import People from "@/pages/People";
import Training from "@/pages/Training";
import MyTraining from "@/pages/MyTraining";
import Assets from "@/pages/Assets";
import Haccp from "@/pages/Haccp";
import SousVide from "@/pages/SousVide";
import ActivityLog from "@/pages/ActivityLog";
import Notifications from "@/pages/Notifications";
import Settings from "@/pages/Settings";
import ChangePassword from "@/pages/ChangePassword";
import Profile from "@/pages/Profile";
import Branding from "@/pages/Branding";
import Reporting from "@/pages/Reporting";
import AdminConsole from "@/pages/AdminConsole";
import SuperAdminPortal from "@/pages/SuperAdminPortal";
import OperatingHours from "@/pages/OperatingHours";
import DailyApproval from "@/pages/DailyApproval";
import QrCodes from "@/pages/QrCodes";
import QrLanding from "@/pages/QrLanding";
import AllergenQr from "@/pages/AllergenQr";
import AllergenMenu from "@/pages/AllergenMenu";
import AllergenPreview from "@/pages/AllergenPreview";
import KitchenTablet from "@/pages/KitchenTablet";

function FullLoader() {
  return <BrandSplash />;
}

function Protected({ children, perm, module }) {
  const { user, loading, modules, moduleEnabled } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "superadmin") return <Navigate to="/admin" replace />;
  if (user.personal_mode) return <Navigate to="/my" replace />;
  if (perm && !user.permissions_effective?.includes(perm)) return <Layout><AccessDenied /></Layout>;
  if (module && modules === null) return <FullLoader />;
  if (module && !moduleEnabled(module)) return <Layout><ModuleDisabled /></Layout>;
  return <Layout>{children}</Layout>;
}

function SuperProtected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "superadmin") return <Navigate to="/dashboard" replace />;
  return children;
}

// Authentication-only gate: any signed-in user (ordinary OR platform superadmin) may pass; only
// unauthenticated users are sent to /login. Used for account routes shared by both principals
// (e.g. Change password), WITHOUT weakening the customer-app superadmin→/admin redirect elsewhere.
function AuthOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AccessDenied() {
  return (
    <div className="cc-card p-10 text-center max-w-md mx-auto mt-10" data-testid="access-denied">
      <h2 className="font-display text-xl font-bold text-slate-800">No access</h2>
      <p className="text-sm text-slate-500 mt-2">You don't have permission to view this section. Contact your manager if you think this is a mistake.</p>
    </div>
  );
}

function ModuleDisabled() {
  return (
    <div className="cc-card p-10 text-center max-w-md mx-auto mt-10" data-testid="module-disabled">
      <h2 className="font-display text-xl font-bold text-slate-800">Feature not enabled</h2>
      <p className="text-sm text-slate-500 mt-2">This module isn't switched on for your organisation. Contact your administrator if you need access.</p>
    </div>
  );
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  const { search } = useLocation();
  if (loading || user === null) return <FullLoader />;
  if (user) {
    const next = new URLSearchParams(search).get("next") || "";
    const safe = /^\/(?!\/)[\w\-/]*$/.test(next) ? next : null; // same-site paths only
    return <Navigate to={user.role === "superadmin" ? "/admin" : safe || (user.personal_mode ? "/my" : "/dashboard")} replace />;
  }
  return children;
}

function PersonOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<PublicOnly><Welcome /></PublicOnly>} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/signup" element={<PublicOnly><PersonalSignup /></PublicOnly>} />
      <Route path="/my" element={<PersonOnly><MyCaterCTRL /></PersonOnly>} />
      <Route path="/perks" element={<Protected perm="perks.redeem"><Perks /></Protected>} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/q/:token" element={<QrLanding />} />
      <Route path="/allergens/:token" element={<AllergenMenu />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/branding" element={<Protected perm="manage_settings"><Branding /></Protected>} />
      <Route path="/orders" element={<Protected perm="view_orders" module="orders"><Orders /></Protected>} />
      <Route path="/deliveries" element={<Protected perm="view_deliveries" module="deliveries"><Deliveries /></Protected>} />
      <Route path="/recipes" element={<Protected perm="view_recipes" module="recipes"><Recipes /></Protected>} />
      <Route path="/menus" element={<Protected perm="view_menus" module="menus"><Menus /></Protected>} />
      <Route path="/temperatures" element={<Protected perm="view_temperature" module="temperatures"><Temperatures /></Protected>} />
      <Route path="/cleaning" element={<Protected perm="view_cleaning" module="cleaning"><Cleaning /></Protected>} />
      <Route path="/prep" element={<Protected perm="view_prep" module="prep"><Prep /></Protected>} />
      <Route path="/food-safety-setup" element={<Protected><FoodSafetySetup /></Protected>} />
      <Route path="/checklists" element={<Protected perm="view_checklists" module="checklists"><Checklists /></Protected>} />
      <Route path="/operating" element={<Protected perm="manage_site_operating_status" module="operating"><OperatingHours /></Protected>} />
      <Route path="/workforce" element={<Protected perm="view_rota" module="rota"><Workforce /></Protected>} />
      <Route path="/my-rota" element={<Protected><MyRota /></Protected>} />
      <Route path="/change-password" element={<AuthOnly><ChangePassword /></AuthOnly>} />
      <Route path="/my-tasks" element={<Protected><MyTasks /></Protected>} />
      <Route path="/staff" element={<Protected perm="view_staff" module="workforce"><Staff /></Protected>} />
      <Route path="/people" element={<Protected perm="view_people"><People /></Protected>} />
      <Route path="/training" element={<Protected perm="view_training" module="training"><Training /></Protected>} />
      <Route path="/my-training" element={<Protected module="training"><MyTraining /></Protected>} />
      <Route path="/activity" element={<Protected perm="view_activity"><ActivityLog /></Protected>} />
      <Route path="/notifications" element={<Protected><Notifications /></Protected>} />
      <Route path="/settings" element={<Protected perm="manage_settings"><Settings /></Protected>} />
      <Route path="/reporting" element={<Protected perm="view_reports" module="reports"><Reporting /></Protected>} />
      <Route path="/assets" element={<Protected perm="view_assets" module="assets"><Assets /></Protected>} />
      <Route path="/haccp" element={<Protected perm="view_haccp" module="haccp"><Haccp /></Protected>} />
      <Route path="/sous-vide" element={<Protected perm="view_temperature" module="haccp"><SousVide /></Protected>} />
      <Route path="/daily-approval" element={<Protected perm="view_daily_approval" module="daily_approval"><DailyApproval /></Protected>} />
      <Route path="/qr-codes" element={<Protected perm="manage_qr" module="qr"><QrCodes /></Protected>} />
      <Route path="/allergen-qr" element={<Protected perm="manage_allergen_qr" module="allergen_qr"><AllergenQr /></Protected>} />
      <Route path="/allergen-qr-preview/:id" element={<Protected perm="manage_allergen_qr" module="allergen_qr"><AllergenPreview /></Protected>} />
      <Route path="/admin" element={<SuperProtected><AdminConsole /></SuperProtected>} />
      <Route path="/superadmin" element={<SuperProtected><SuperAdminPortal /></SuperProtected>} />
      <Route path="/tablet" element={<KitchenTablet />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

const ROUTE_TITLES = {
  "/": "CaterCTRL",
  "/login": "Sign in | CaterCTRL",
  "/set-password": "Set your password | CaterCTRL",
  "/reset-password": "Reset password | CaterCTRL",
  "/dashboard": "Dashboard | CaterCTRL",
  "/orders": "Orders | CaterCTRL",
  "/recipes": "Recipes | CaterCTRL",
  "/menus": "Menus | CaterCTRL",
  "/temperatures": "Temperatures | CaterCTRL",
  "/cleaning": "Cleaning | CaterCTRL",
  "/prep": "Prep | CaterCTRL",
  "/checklists": "Checklists | CaterCTRL",
  "/operating": "Opening Hours | CaterCTRL",
  "/daily-approval": "Daily Approval | CaterCTRL",
  "/qr-codes": "Kitchen QR Codes | CaterCTRL",
  "/allergen-qr": "Customer Allergen QR | CaterCTRL",
  "/staff": "Staff | CaterCTRL",
  "/activity": "Activity | CaterCTRL",
  "/notifications": "Notifications | CaterCTRL",
  "/settings": "Settings | CaterCTRL",
  "/reporting": "Reporting | CaterCTRL",
  "/admin": "Platform Admin | CaterCTRL",
  "/superadmin": "Platform Admin | CaterCTRL",
};

function RouteTitle() {
  const { pathname } = useLocation();
  useEffect(() => { document.title = ROUTE_TITLES[pathname] || "CaterCTRL"; }, [pathname]);
  return null;
}

export default function App() {
  useEffect(() => {
    // React has committed the real route/loading/error UI. Never wait for an animation.
    const frame = requestAnimationFrame(() => window.dismissCaterCTRLSplash?.());
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <RouteTitle />
            <ReleaseGate>
              <AppRoutes />
              <SyncStatus />
              <OfflineErrorToaster />
            </ReleaseGate>
            <Toaster position="top-center" richColors />
          </BrowserRouter>
        </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}


